import React, { useState } from 'react';
import { saveUserProfile, validateCpf, validatePhoneBrasil, validatePhoneCuba, claimWelcomeBonus, logClicksHistory, subscribeToPush, notifyAdmin, ADMIN_EMAIL } from '../services/authService';
import { useAuth } from '../context/AuthContext';

// ── Toast global ──────────────────────────────────────────────
let _setToast = null;
export const toast = (msg, type = 'success') => _setToast?.({ msg, type });

export const ToastProvider = () => {
  const [t, setT] = useState(null);
  _setToast = (data) => { setT(data); setTimeout(() => setT(null), 3200); };
  if (!t) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
      background: t.type === 'error' ? 'var(--error)' : 'var(--success)',
      color: 'white', padding: '12px 20px', borderRadius: 10,
      fontWeight: 600, fontSize: 14, zIndex: 9999,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      maxWidth: '90vw', textAlign: 'center',
    }}>{t.msg}</div>
  );
};

export const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label>{label}</label>}
    {children}
    {error && <p style={{ color: 'var(--error)', fontSize: 12, marginTop: 4 }}>{error}</p>}
  </div>
);

export const PrimaryBtn = ({ loading, children, onClick, disabled, style = {} }) => (
  <button className="btn-primary" onClick={onClick} disabled={loading || disabled} style={style}>
    {loading ? <span className="spinner" /> : children}
  </button>
);

export const SectionLabel = ({ children }) => (
  <p className="section-label">{children}</p>
);

// ── Modal "Completa tu perfil" ────────────────────────────────
export const CompleteProfileModal = ({ uid, userName, userEmail, onComplete, onClose }) => {
  const { refreshUser } = useAuth();
  const needsName = !userName || userName.trim() === '';

  const [ubicacion, setUbicacion] = useState('');
  const [name, setName] = useState('');
  const [cpf, setCpf] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [activatingPush, setActivatingPush] = useState(false);

  const selectUbicacion = (val) => {
    setUbicacion(val);
    setCpf('');
    setPhone('');
    setErrors(p => ({ ...p, ubicacion: '', cpf: '', phone: '' }));
  };

  const formatCpfInput = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 11);
    return d
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
      .replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
      .replace(/(\d{3})(\d{3})/, '$1.$2')
      .replace(/(\d{3})/, '$1');
  };

  // El código de país (55/53) es fijo y no editable — el usuario solo escribe
  // el número local, así se evita que lo tipee sin el código y se guarde mal.
  const countryCode = ubicacion === 'cuba' ? '53' : '55';
  const fullPhoneDigits = () => countryCode + phone.replace(/\D/g, '');

  const formatPhone = (val) => {
    if (ubicacion === 'cuba') {
      const d = val.replace(/\D/g, '').slice(0, 8); // 8 dígitos locales
      if (d.length <= 4) return d;
      return `${d.slice(0,4)} ${d.slice(4)}`;
    }
    const d = val.replace(/\D/g, '').slice(0, 11); // DDD (2) + número (9)
    if (d.length <= 2) return d;
    if (d.length <= 7) return `${d.slice(0,2)} ${d.slice(2)}`;
    return `${d.slice(0,2)} ${d.slice(2,7)}-${d.slice(7)}`;
  };

  const validate = () => {
    const e = {};
    if (!ubicacion) e.ubicacion = 'Selecciona dónde estás.';
    if (needsName && !name.trim()) e.name = 'Por favor escribe tu nombre completo.';
    if (ubicacion === 'brasil') {
      if (!validateCpf(cpf)) e.cpf = 'CPF inválido. Verifica los números.';
      if (!validatePhoneBrasil(fullPhoneDigits())) e.phone = 'El teléfono debe tener 11 dígitos: DDD + número.';
    } else if (ubicacion === 'cuba') {
      if (!validatePhoneCuba(fullPhoneDigits())) e.phone = 'El teléfono debe tener 8 dígitos.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (!uid) throw new Error("No hay un usuario autenticado.");

      await saveUserProfile(uid, {
        cpf: ubicacion === 'brasil' ? cpf : undefined,
        phone: fullPhoneDigits(),
        name: needsName ? name.trim() : undefined,
        ubicacion,
      });

      // El bono de bienvenida es idempotente: el RPC solo suma los 50 CLICKs
      // la primera vez (welcome_bonus_claimed pasa de false a true). Si el
      // usuario ya lo había recibido (edición posterior, reintento), no
      // vuelve a otorgarse.
      const bonusGranted = await claimWelcomeBonus(uid);
      if (bonusGranted) {
        await logClicksHistory(uid, 50, 'bienvenida');
        toast('¡Perfil completado! Recibiste 50 CLICKs de bienvenida 🎉');
        notifyAdmin(`👤 Nuevo cliente registrado: ${needsName ? name.trim() : userName}`, '/?tab=more&admin=usuarios');
      } else {
        toast('¡Perfil actualizado! ✅');
      }

      // Refresca el perfil en el contexto de auth para que profileComplete
      // se actualice de inmediato y el candado desaparezca sin recargar.
      await refreshUser();

      // Justo después del bono de bienvenida, ofrece activar notificaciones
      // (punto 1d) -- quien rechace o cierre puede activarlas luego desde
      // MoreScreen. Si no hubo bono (edición posterior), se cierra directo.
      if (bonusGranted) {
        setShowPushPrompt(true);
      } else {
        onComplete();
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      if (err.code === '23505') {
        const detail = `${err.details || ''} ${err.message || ''}`.toLowerCase();
        if (detail.includes('cpf')) {
          toast('Este CPF ya está registrado. Si es tuyo, contáctanos por WhatsApp.', 'error');
        } else if (detail.includes('phone')) {
          toast('Este teléfono ya está registrado. Si es tuyo, contáctanos por WhatsApp.', 'error');
        } else {
          toast(`Error: ${err.message || err.code || 'desconocido'}`, 'error');
        }
      } else {
        toast(`Error: ${err.message || err.code || 'desconocido'}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePush = async () => {
    setActivatingPush(true);
    await subscribeToPush(uid, userEmail === ADMIN_EMAIL);
    setActivatingPush(false);
    setShowPushPrompt(false);
    onComplete();
  };

  if (showPushPrompt) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        zIndex: 200, display: 'flex', alignItems: 'flex-end',
        backdropFilter: 'blur(4px)',
      }}>
        <div style={{
          background: 'var(--navy-light)', borderRadius: '20px 20px 0 0',
          width: '100%', padding: '28px 20px 36px', textAlign: 'center',
        }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>🔔</p>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--white)', marginBottom: 10 }}>Activa las notificaciones</h3>
          <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 24 }}>
            Activa las notificaciones para enterarte al instante cuando ganes CLICKs, subas de nivel o haya una promoción especial.
          </p>
          <PrimaryBtn loading={activatingPush} onClick={handleActivatePush} style={{ marginBottom: 10 }}>
            Activar
          </PrimaryBtn>
          <button onClick={() => { setShowPushPrompt(false); onComplete(); }} style={{
            width: '100%', background: 'none', color: 'var(--muted)', fontSize: 13, fontWeight: 600, padding: '10px 0',
          }}>Ahora no</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      zIndex: 200, display: 'flex', alignItems: 'flex-end',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: 'var(--navy-light)', borderRadius: '20px 20px 0 0',
        width: '100%', padding: '24px 20px 36px',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <p style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700, letterSpacing: 1 }}>UN PASO MÁS</p>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: 'var(--white)', marginTop: 2 }}>Completa tu perfil</h3>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background: 'none', color: 'var(--muted)', fontSize: 22 }}>✕</button>
          )}
        </div>

        <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24, lineHeight: 1.5 }}>
          Solo necesitamos algunos datos para procesar tu solicitud y asignarte CLICKs.
        </p>

        {/* ¿Dónde estás? */}
        <Field label="¿DÓNDE ESTÁS? *" error={errors.ubicacion}>
          <div style={{ display: 'flex', gap: 10 }}>
            {[{ value: 'brasil', label: '🇧🇷 Brasil' }, { value: 'cuba', label: '🇨🇺 Cuba' }].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => selectUbicacion(opt.value)}
                style={{
                  flex: 1, textAlign: 'center', padding: '14px 12px', borderRadius: 12,
                  fontWeight: 700, fontSize: 14,
                  background: ubicacion === opt.value ? 'rgba(255,107,0,0.18)' : 'var(--navy-card)',
                  color: ubicacion === opt.value ? 'var(--orange)' : 'var(--off-white)',
                  border: `1.5px solid ${ubicacion === opt.value ? 'var(--orange)' : 'var(--navy-border)'}`,
                }}
              >{opt.label}</button>
            ))}
          </div>
        </Field>

        {/* Correo verificado */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, letterSpacing: 1, color: 'var(--muted)', fontWeight: 700 }}>
            CORREO VERIFICADO ✅
          </label>
          <input value={userEmail || ''} readOnly
            style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--muted)', cursor: 'default' }} />
        </div>

        {/* Nombre — solo si no vino de Google */}
        {ubicacion && needsName && (
          <Field label="NOMBRE COMPLETO *" error={errors.name}>
            <input
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }}
              placeholder="Tu nombre completo"
            />
          </Field>
        )}

        {/* CPF — solo Brasil */}
        {ubicacion === 'brasil' && (
          <Field label="CPF *" error={errors.cpf}>
            <input
              value={cpf}
              onChange={e => { setCpf(formatCpfInput(e.target.value)); setErrors(p => ({ ...p, cpf: '' })); }}
              placeholder="000.000.000-00"
              inputMode="numeric"
            />
          </Field>
        )}

        {/* Teléfono — código de país fijo, el usuario solo escribe el número local */}
        {ubicacion && (
          <Field label="TELÉFONO *" error={errors.phone}>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{
                padding: '13px 14px', background: 'var(--navy-card)',
                border: '1px solid var(--navy-border)', borderRadius: 'var(--radius-sm)',
                color: 'var(--off-white)', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap',
              }}>+{countryCode}</span>
              <input
                value={phone}
                onChange={e => { setPhone(formatPhone(e.target.value)); setErrors(p => ({ ...p, phone: '' })); }}
                placeholder={ubicacion === 'cuba' ? '5xxx xxxx' : '11 99999-9999'}
                inputMode="tel"
                style={{ flex: 1 }}
              />
            </div>
          </Field>
        )}

        <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 }}>
          🔒 Tus datos están protegidos. Tu información no aparecerá en los mensajes de WhatsApp.
        </p>

        <PrimaryBtn loading={loading} onClick={handleSave}>
          Guardar y continuar →
        </PrimaryBtn>
      </div>
    </div>
  );
};
